import { useCallback, useRef, useState } from 'react';

/**
 * Pan and zoom for the canvas board.
 *
 * One transform (`translate(x, y) scale(zoom)`) applied to a single layer, so
 * cards, the dotted grid and hit-testing all stay in step: a card's position is
 * stored in board coordinates once and never recomputed per zoom level. The
 * inverse (`toBoard`) is the only place screen pixels turn back into board
 * pixels, which is what keeps dragging a card at 40% zoom feel 1:1.
 */

export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 2.5;
/** Breathing room around the content when fitting, in board pixels. */
const FIT_PADDING = 96;

/**
 * How far in the opening fit is allowed to go.
 *
 * A single desktop capture would otherwise fill the board at nearly 1:1, which
 * reads as "you are inside one screenshot" rather than "here is your run" —
 * and leaves nowhere to drag anything to. Zooming in is one scroll away;
 * realising you are zoomed in is not.
 */
const OPENING_ZOOM_MAX = 0.55;

const clampZoom = (zoom: number) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/** The zoom at which `content` fits a viewport of `size`, padded. */
function fitZoom(
  content: Box,
  size: { width: number; height: number },
  ceiling: number,
): number {
  return clampZoom(
    Math.min(
      ceiling,
      size.width / (content.width + FIT_PADDING * 2),
      size.height / (content.height + FIT_PADDING * 2),
    ),
  );
}

/** A flight's length. Long enough to follow, short enough that the room has
 *  not moved on by the time it lands. */
const FLIGHT_MS = 850;

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useCanvasViewport() {
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, zoom: 1 });
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  /** Screen point (relative to the board element) → board coordinates. */
  const toBoard = useCallback((screenX: number, screenY: number) => {
    const { x, y, zoom } = viewportRef.current;
    return { x: (screenX - x) / zoom, y: (screenY - y) / zoom };
  }, []);

  /**
   * Zooms about a fixed screen point, so the board pixel under the cursor stays
   * under the cursor. Zooming about the origin instead makes the content appear
   * to slide away, and users compensate by panning after every zoom.
   */
  const zoomAt = useCallback(
    (factor: number, screenX: number, screenY: number) => {
      setViewport((prev) => {
        const zoom = clampZoom(prev.zoom * factor);
        const applied = zoom / prev.zoom;
        return {
          x: screenX - (screenX - prev.x) * applied,
          y: screenY - (screenY - prev.y) * applied,
          zoom,
        };
      });
    },
    [],
  );

  const panBy = useCallback((dx: number, dy: number) => {
    setViewport((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
  }, []);

  /**
   * Frames `content` inside a viewport of `size`.
   *
   * `ceiling` caps how far in it may go: the opening fit stays pulled back, but
   * the Fit button the user presses deliberately goes to whatever actually
   * fits, up to 1:1.
   */
  const fitTo = useCallback(
    (
      content: Box | null,
      size: { width: number; height: number },
      ceiling = 1,
    ) => {
      if (!content || content.width <= 0 || content.height <= 0) {
        setViewport({ x: 0, y: 0, zoom: 1 });
        return;
      }

      const zoom = fitZoom(content, size, ceiling);

      setViewport({
        x: size.width / 2 - (content.x + content.width / 2) * zoom,
        y: size.height / 2 - (content.y + content.height / 2) * zoom,
        zoom,
      });
    },
    [],
  );

  /**
   * Frames `content` by its width alone, its top edge `topShare` of the way
   * down a viewport of `size`.
   *
   * For a page that is meant to be read from the top: a full-page capture is
   * many screens tall, and fitting its height shrank it to a sliver in the
   * middle of the board. The rest runs on below the fold, a scroll away.
   */
  const fitTop = useCallback(
    (
      content: Box,
      size: { height: number; width: number },
      { ceiling, topShare }: { ceiling: number; topShare: number },
    ) => {
      if (content.width <= 0 || content.height <= 0) return;
      const zoom = clampZoom(
        Math.min(ceiling, size.width / (content.width + FIT_PADDING * 2)),
      );
      setViewport({
        x: size.width / 2 - (content.x + content.width / 2) * zoom,
        y: size.height * topShare - content.y * zoom,
        zoom,
      });
    },
    [],
  );

  const flight = useRef<number | null>(null);

  /** Stops a flight where it is — the researcher has taken the board. */
  const cancelFlight = useCallback(() => {
    if (flight.current !== null) cancelAnimationFrame(flight.current);
    flight.current = null;
  }, []);

  /**
   * Glides the view so board point `toCentre` sits in the middle of a viewport
   * of `size`, at `toZoom`.
   *
   * Zoom changes in log space so it reads as even speed, the centre travels
   * along a straight line in board space, and a long trip pulls back a little
   * mid-way — the way a camera moves between two far-apart shots — so the
   * researcher keeps their bearings. Jumps straight there under reduced
   * motion.
   */
  const glide = useCallback(
    (
      toCentre: { x: number; y: number },
      toZoom: number,
      size: { height: number; width: number },
    ) => {
      cancelFlight();
      const from = viewportRef.current;
      const fromCentre = {
        x: (size.width / 2 - from.x) / from.zoom,
        y: (size.height / 2 - from.y) / from.zoom,
      };
      const place = (centre: { x: number; y: number }, zoom: number) => ({
        x: size.width / 2 - centre.x * zoom,
        y: size.height / 2 - centre.y * zoom,
        zoom,
      });

      if (prefersReducedMotion()) {
        setViewport(place(toCentre, toZoom));
        return;
      }

      // How far the trip is, in viewport widths at the lower of the two
      // zooms — past one width, pull back by up to a quarter mid-flight.
      const distance =
        (Math.hypot(toCentre.x - fromCentre.x, toCentre.y - fromCentre.y) *
          Math.min(from.zoom, toZoom)) /
        Math.max(size.width, 1);
      const dip = Math.min(0.25, Math.max(0, (distance - 1) * 0.12));

      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / FLIGHT_MS);
        const e = easeInOutCubic(t);
        const zoom =
          Math.exp(
            Math.log(from.zoom) + (Math.log(toZoom) - Math.log(from.zoom)) * e,
          ) *
          (1 - dip * Math.sin(Math.PI * e));
        setViewport(
          place(
            {
              x: fromCentre.x + (toCentre.x - fromCentre.x) * e,
              y: fromCentre.y + (toCentre.y - fromCentre.y) * e,
            },
            zoom,
          ),
        );
        flight.current = t < 1 ? requestAnimationFrame(step) : null;
      };
      flight.current = requestAnimationFrame(step);
    },
    [cancelFlight],
  );

  /**
   * Glides the board so `box` sits in the middle of a viewport of `size`,
   * taking up about `share` of it.
   */
  const flyTo = useCallback(
    (
      box: Box,
      size: { height: number; width: number },
      {
        maxZoom = 1.1,
        share = 0.55,
      }: { maxZoom?: number; share?: number } = {},
    ) => {
      const toZoom = clampZoom(
        Math.min(
          maxZoom,
          (size.width * share) / Math.max(box.width, 1),
          (size.height * share) / Math.max(box.height, 1),
        ),
      );
      glide(
        { x: box.x + box.width / 2, y: box.y + box.height / 2 },
        toZoom,
        size,
      );
    },
    [glide],
  );

  /**
   * `fitTo`, as a flight: lands exactly where `fitTo` would jump to, for
   * content that changed shape while someone was watching it.
   */
  const flyToFit = useCallback(
    (content: Box, size: { height: number; width: number }, ceiling = 1) => {
      if (content.width <= 0 || content.height <= 0) return;
      glide(
        {
          x: content.x + content.width / 2,
          y: content.y + content.height / 2,
        },
        fitZoom(content, size, ceiling),
        size,
      );
    },
    [glide],
  );

  /** Glides back to a view the board was at — where the researcher was
   *  before something took them elsewhere. */
  const flyToViewport = useCallback(
    (to: Viewport, size: { height: number; width: number }) => {
      glide(
        {
          x: (size.width / 2 - to.x) / to.zoom,
          y: (size.height / 2 - to.y) / to.zoom,
        },
        to.zoom,
        size,
      );
    },
    [glide],
  );

  return {
    cancelFlight,
    fitTo,
    fitTop,
    flyTo,
    flyToFit,
    flyToViewport,
    panBy,
    toBoard,
    viewport,
    zoomAt,
  };
}

export { FLIGHT_MS, MAX_ZOOM, MIN_ZOOM, OPENING_ZOOM_MAX };
